import { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import { Dropdown } from "@/config/antd";
import Delete from "@/components/common/alerts/Delete";
import Success from "@/components/common/alerts/Success";
import { useLoader } from "@/hooks/LoaderProvider";
import ScheduleDialog from "@/components/shared/dialogs/Schedule_Dialog/Schedule_Dialog";
import { useDispatch } from "react-redux";
import { searchUserByFieldAsync } from "@/store/redux/slices/user/user_slice";
import { removeScheduleFromSubEvent } from "@/store/redux/slices/event/sub_event_slice";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import Button from "@/components/common/Button";

const ScheduleCard = ({
  schedule,
  subEventId,
  onScheduleSuccess,
  onScheduleError,
}) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [speakerInfo, setSpeakerInfo] = useState([]);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const { showLoader, hideLoader } = useLoader();
  const dispatch = useDispatch();

  const fetchSpeakersInfo = useCallback(async () => {
    try {
      const speakerPromises = schedule.speakers.map((speakerId) =>
        dispatch(
          searchUserByFieldAsync({ key: "_id", value: speakerId })
        ).unwrap()
      );

      const speakerResults = await Promise.all(speakerPromises);

      // Map through results to extract speaker data
      const speakersData = speakerResults.flatMap(
        (result) => result.data || []
      );

      setSpeakerInfo(speakersData);
    } catch (error) {
      console.error("Error fetching speaker information:", error);
    }
  }, [dispatch, schedule?.speakers]);

  useEffect(() => {
    if (schedule?.speakers && schedule.speakers.length > 0) {
      fetchSpeakersInfo();
    }
  }, [schedule, fetchSpeakersInfo]);

  const getSpeakerNames = () => {
    if (speakerInfo.length === 0)
      return schedule?.assignedTo || "No speakers assigned";
    return speakerInfo.map((speaker) => speaker.fullname).join(", ");
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleCloseDelete = () => {
    setIsDeleteModalOpen(false);
  };

  const handleDeleteProceed = async () => {
    setIsDeleteModalOpen(false);
    showLoader();

    // Finding the actual schedule ID
    const actualScheduleId = schedule._id || schedule.id;

    console.log("Schedule and subEvent ID's:", actualScheduleId, subEventId);

    try {
      // Use the new endpoint to remove the schedule
      const response = await dispatch(
        removeScheduleFromSubEvent({
          subEventId: subEventId,
          scheduleId: actualScheduleId,
        })
      ).unwrap();

      console.log("respose to delete schedule ", response);

      setSuccessMessage("Schedule has been removed successfully.");
      setIsSuccessModalOpen(true);

      // Call parent success handler if provided
      if (onScheduleSuccess && typeof onScheduleSuccess === "function") {
        onScheduleSuccess("remove", {
          subEventId,
          scheduleId: actualScheduleId,
        });
      }
    } catch (error) {
      console.error("Failed to remove schedule:", error);

      // Call parent error handler if provided
      if (onScheduleError && typeof onScheduleError === "function") {
        onScheduleError(
          "remove",
          error?.message || "Failed to remove schedule"
        );
      }
    } finally {
      hideLoader();
    }
  };

  const handleModifySchedule = () => {
    setDialogMode("modify");

    // Finding the actual schedule ID - it could be at different properties
    // based on console logs and API structure examination
    const actualScheduleId = schedule._id || schedule.id;

    const scheduleData = {
      ...schedule,
      // Store the ID in a separate property to avoid confusion with the subEvent ID
      scheduleId: actualScheduleId,
      subEventId: subEventId,
      speakers: speakerInfo.length > 0 ? speakerInfo : schedule.speakers,
    };
    setSelectedSchedule(scheduleData);
    setIsScheduleDialogOpen(true);
  };

  const handleScheduleSuccess = (mode, data) => {
    // Close the dialog
    setIsScheduleDialogOpen(false);

    // Use the parent handler if provided
    if (onScheduleSuccess && typeof onScheduleSuccess === "function") {
      onScheduleSuccess(mode, data);
    }

    // Refresh data if needed
    fetchSpeakersInfo();
  };

  const handleScheduleError = (mode, errorMsg) => {
    console.error(
      `Schedule ${mode === "add" ? "add" : "update"} failed:`,
      errorMsg
    );

    // Use the parent handler if provided
    if (onScheduleError && typeof onScheduleError === "function") {
      onScheduleError(mode, errorMsg);
    }
  };

  const handleSuccessClose = () => {
    setIsSuccessModalOpen(false);
  };

  // Add menu configuration for MoreOptions
  const moreMenu = {
    items: [
      {
        key: "modify",
        label: "Modify Schedule",
        onClick: handleModifySchedule,
      },
      {
        key: "delete",
        label: "Delete Schedule",
        danger: true,
        onClick: handleDelete,
      },
    ],
  };

  return (
    <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
      <div className="flex items-center gap-4">
        {/* Timeline Section */}
        <div className="flex flex-col">
          <span className="font-none text-primary text-small-xs uppercase">
            Timeline
          </span>
          <span className="font-bold text-bold">
            {schedule.duration?.startTime || "No time set"}
          </span>
        </div>

        {/* Event Section */}
        <div className="flex flex-col">
          <span className="font-bold text-regular">{schedule?.name}</span>
          <span className="text-lighterGrey text-small-xs font-bold">
            {getSpeakerNames()}
          </span>
        </div>
      </div>

      {/* Actions Section */}
      <div className="flex items-center">
        <Button
          text="REMOVE"
          className="mr-2 border border-primary text-small w-[85px] h-[34px]"
          customBgColor="white"
          customTextColor="primary"
          onClick={handleDelete}
          fontWeight="none"
        />

        <Dropdown menu={moreMenu} placement="bottomRight" trigger={["click"]}>
          <div onClick={(e) => e.preventDefault()}>
            <CustomMoreOptions
              variant="filled"
              orientation="vertical"
              color="black"
              size="small"
            />
          </div>
        </Dropdown>
      </div>

      <Delete
        isVisible={isDeleteModalOpen}
        onClose={handleCloseDelete}
        onProceed={handleDeleteProceed}
        header="Remove Schedule?"
        paragraph="Removing this schedule means you will have to make modifications to the existing ones."
        proceedButtonText="Proceed"
        cancelButtonText="Cancel"
      />

      <ScheduleDialog
        isVisible={isScheduleDialogOpen}
        onClose={() => setIsScheduleDialogOpen(false)}
        schedule={selectedSchedule}
        dialogMode={dialogMode}
        onSuccess={handleScheduleSuccess}
        onError={handleScheduleError}
      />

      <Success
        isVisible={isSuccessModalOpen}
        onClose={handleSuccessClose}
        header="Schedule Removed"
        paragraph={successMessage}
        buttonText="Close"
        onButtonClick={handleSuccessClose}
      />
    </div>
  );
};

ScheduleCard.propTypes = {
  schedule: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
    assignedTo: PropTypes.string,
    speakers: PropTypes.array,
    _id: PropTypes.string,
    duration: PropTypes.shape({
      startTime: PropTypes.string,
      endTime: PropTypes.string,
    }),
  }),
  subEventId: PropTypes.string,
  onScheduleSuccess: PropTypes.func,
  onScheduleError: PropTypes.func,
};

export default ScheduleCard;
